import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Heart, User, Eye, EyeOff } from "lucide-react";
import { COUNTRIES, getPhoneCode } from "../lib/countries";

export default function RegisterPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [params] = useSearchParams();
  const [type, setType] = useState(params.get("type") === "joint" ? "joint" : "individual");
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "", password: "",
    partner_name: "", partner_email: "", partner_phone: "",
  });
  const [countryCode, setCountryCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setForm({ ...form, country: selectedCountry });
    const code = getPhoneCode(selectedCountry);
    setCountryCode(code);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ ...form, email: form.email.trim().toLowerCase(), account_type: type });
      toast.success("Welcome to Duocapital", { description: type === "joint" ? "Your Joint Account is ready." : "Your account is ready." });
      nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen px-6 py-16" data-testid="register-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="font-display text-3xl font-black inline-block">Duo<span className="gold-text">capital</span></Link>
        <div className="mt-8 mb-8">
          <div className="label-eyebrow">Create account</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black mt-3 leading-tight">Grow together — Duocapital supports shared investment accounts for couples and business partners.</h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <button onClick={() => setType("individual")} className={`p-5 rounded-xl text-left transition-colors ${type === "individual" ? "bg-[#D4AF37]/10 border border-[#D4AF37]" : "glass hover:bg-white/5"}`} data-testid="acct-individual">
            <User className="w-5 h-5 text-[#D4AF37] mb-2" />
            <div className="font-semibold">Individual</div>
            <div className="text-xs text-white/60 mt-1">Solo investor account</div>
          </button>
          <button onClick={() => setType("joint")} className={`p-5 rounded-xl text-left transition-colors ${type === "joint" ? "bg-[#D4AF37]/10 border border-[#D4AF37]" : "glass hover:bg-white/5"}`} data-testid="acct-joint">
            <Heart className="w-5 h-5 text-[#D4AF37] mb-2" />
            <div className="font-semibold">Joint Account 💑</div>
            <div className="text-xs text-white/60 mt-1">Invest with your partner</div>
          </button>
        </div>

        <form onSubmit={submit} className="glass-strong rounded-2xl p-8 space-y-5" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name" value={form.full_name} onChange={onChange("full_name")} testId="reg-name" required />
            <Field label="Email" type="email" value={form.email} onChange={onChange("email")} testId="reg-email" required />
            <div className="col-span-2 sm:col-span-1 min-w-0">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60 block mb-2">Country</span>
                <select 
                  value={form.country} 
                  onChange={handleCountryChange} 
                  required 
                  data-testid="reg-country"
                  className="w-full min-w-0 px-4 py-3 rounded-lg bg-[#0A0E27]/90 border border-white/20 text-white appearance-none focus:border-[#D4AF37] outline-none"
                >
                  <option value="">Select your country...</option>
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.name} className="text-black bg-white">
                      {country.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <PhoneField 
              label="Phone" 
              value={form.phone} 
              onChange={onChange("phone")} 
              countryCode={countryCode}
              testId="reg-phone" 
              required 
            />
            <Field label="Password" type="password" value={form.password} onChange={onChange("password")} testId="reg-password" required />
          </div>

          {type === "joint" && (
            <>
              <div className="h-px bg-white/10 my-4"></div>
              <div className="text-sm text-[#D4AF37] flex items-center gap-2"><Heart className="w-4 h-4" /> Partner Details</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Partner Full Name" value={form.partner_name} onChange={onChange("partner_name")} testId="reg-partner-name" required />
                <Field label="Partner Email" type="email" value={form.partner_email} onChange={onChange("partner_email")} testId="reg-partner-email" required />
                <Field label="Partner Phone" value={form.partner_phone} onChange={onChange("partner_phone")} testId="reg-partner-phone" required />
              </div>
            </>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="register-submit">
            {loading ? "Creating account..." : `Create ${type === "joint" ? "Joint" : "Individual"} Account`}
          </button>
          <div className="text-sm text-white/60 text-center">
            Already have an account? <Link to="/login" className="text-[#D4AF37]">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";

  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 block mb-2">{label}</span>
      <div className="relative">
        <input type={isPassword && showPassword ? "text" : type} value={value} onChange={onChange} required={required} className={`w-full px-4 py-3 rounded-lg ${isPassword ? "pr-12" : ""}`} data-testid={testId} />
        {isPassword && (
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors">
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

function PhoneField({ label, value, onChange, countryCode, required, testId }) {
  return (
    <label className="block min-w-0">
      <span className="text-xs uppercase tracking-widest text-white/60 block mb-2">{label}</span>
      <div className="flex flex-col sm:flex-row gap-2 min-w-0">
        {countryCode && (
          <div className="flex items-center justify-center px-4 py-3 rounded-lg bg-[#0A0E27]/90 border border-white/20 text-white text-sm font-semibold whitespace-nowrap min-w-[100px]">
            {countryCode}
          </div>
        )}
        <input 
          type="tel" 
          value={value} 
          onChange={onChange} 
          required={required} 
          className="flex-1 min-w-0 px-4 py-3 rounded-lg bg-[#0A0E27]/90 border border-white/20 text-white" 
          placeholder={countryCode ? "Enter phone number" : "Select country first"}
          data-testid={testId} 
        />
      </div>
    </label>
  );
}
